import { useState, useEffect, createContext, useContext, useCallback, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { ThemeProvider } from "./hooks/useTheme";
// import OfflineStatus from "./components/OfflineStatus"; // Descomente quando implementar offline

// Páginas críticas (carregamento imediato)
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Páginas com lazy loading (carregamento sob demanda)
const PatientsPage = lazy(() => import("./pages/PatientsPage"));
const PatientDetailPage = lazy(() => import("./pages/PatientDetailPage"));
const AppointmentsPage = lazy(() => import("./pages/AppointmentsPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ImageAnalysisPage = lazy(() => import("./pages/ImageAnalysisPage"));
const FinancesPage = lazy(() => import("./pages/FinancesPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const RemindersPage = lazy(() => import("./pages/RemindersPage"));
const OdontogramPage = lazy(() => import("./pages/OdontogramPage"));
const PatientSelfRegister = lazy(() => import("./pages/PatientSelfRegister"));
const PublicSignTerm = lazy(() => import("./pages/PublicSignTerm"));
const AuditPage = lazy(() => import("./pages/AuditPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({
  baseURL: API,
});

// Add interceptor for auth
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Se offline (erro 503 do Service Worker) e tem token válido, permitir continuar
    if (error.response?.status === 503 && error.response?.data?.error === 'Offline') {
      const token = localStorage.getItem("token");
      if (token) {
        console.log('[Offline Mode] Permitindo acesso offline com token em cache');
        return Promise.resolve({ data: error.response.data, fromCache: true });
      }
    }
    
    if (error.response?.status === 401) {
      // PREVENIR LOOP: Verificar se já está na página de login
      if (window.location.pathname === "/login") {
        return Promise.reject(error);
      }
      
      // Não fazer logout se estiver offline
      if (!navigator.onLine) {
        console.log('[Offline Mode] 401 ignorado - modo offline');
        return Promise.reject(error);
      }
      
      console.log('[Auth] 401 - Redirecionando para login');
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }

    if (token) {
      try {
        const response = await api.get("/auth/me");
        setUser(response.data);
        localStorage.setItem("user", JSON.stringify(response.data));
      } catch (error) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
    
    // Desregistrar Service Worker se existir (causando loop de reload)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function(registrations) {
        for(let registration of registrations) {
          registration.unregister();
          console.log('[PWA] Service Worker unregistered');
        }
      });
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { token, user } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    return user;
  };

  const register = async (name, email, password, role = "dentist") => {
    const response = await api.post("/auth/register", { name, email, password, role });
    
    // Check if registration requires approval (new behavior)
    if (response.data.status === "pending_approval") {
      // Return the response data without setting token/user
      return response.data;
    }
    
    // Old behavior: auto-login after registration
    const { token, user } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    return user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const setUserData = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await api.post("/auth/google/session", { session_id: sessionId });
          const { user, token } = response.data;
          
          // Save token and user to localStorage
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(user));
          
          setUserData(user);
          navigate("/dashboard", { replace: true, state: { user } });
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUserData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Autenticando...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-blue-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

import PageLoader from "./components/PageLoader";
import SuspenseLoader from "./components/SuspenseLoader";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - SYNCHRONOUS check during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <PageLoader />
    <Suspense fallback={<SuspenseLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/patient-register/:token" element={<PatientSelfRegister />} />
        <Route path="/sign-term/:token" element={<PublicSignTerm />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/patients" element={<ProtectedRoute><PatientsPage /></ProtectedRoute>} />
        <Route path="/patients/:patientId" element={<ProtectedRoute><PatientDetailPage /></ProtectedRoute>} />
        <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/image-analysis" element={<ProtectedRoute><ImageAnalysisPage /></ProtectedRoute>} />
        <Route path="/finances" element={<ProtectedRoute><FinancesPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
        <Route path="/odontogram" element={<ProtectedRoute><OdontogramPage /></ProtectedRoute>} />
        <Route path="/audit" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </>
  );
}

import { SidebarProvider } from "./hooks/useSidebar";
import { ClinicSettingsProvider } from "./hooks/useClinicSettings";
import { ReceptionAlertProvider } from "./hooks/useReceptionAlert";
import { ErrorBoundary } from "./components/ErrorBoundary";

function App() {
  // Suprimir erros de ResizeObserver que são warnings do navegador e não afetam funcionalidade
  useEffect(() => {
    const resizeObserverErrHandler = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
        return true;
      }
      return false;
    };
    
    window.addEventListener('error', (e) => {
      if (resizeObserverErrHandler(e)) {
        e.preventDefault();
      }
    });
    
    // Suprimir também no console
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <ClinicSettingsProvider>
              <ReceptionAlertProvider>
                <SidebarProvider>
                  <AppRouter />
                  {/* <OfflineStatus /> */} {/* Descomente quando implementar offline */}
                </SidebarProvider>
              </ReceptionAlertProvider>
            </ClinicSettingsProvider>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
